import type { SelectableItem } from "@/components/screener/DualPanelItemSelector";
import type { ExposureType } from "@/services/api/widget/chart-options";
import type { ContributionColumnDef } from "./contributionColumns";

export const EXPOSURE_PRIMARY_SUFFIX = "_primary";

/** Convert API exposure value (kebab-case) to snake_case exposure type. */
export function normalizeExposureType(value: string): string {
  return value.replace(/-/g, "_");
}

/** Column id for an exposure primary, e.g. asset_allocation_primary. */
export function exposureTypeToColumnId(exposureValue: string): string {
  return `${normalizeExposureType(exposureValue)}${EXPOSURE_PRIMARY_SUFFIX}`;
}

/** Parse column id back to snake_case exposure type, if applicable. */
export function parseExposurePrimaryColumnId(
  columnId: string
): string | null {
  if (!columnId.endsWith(EXPOSURE_PRIMARY_SUFFIX)) return null;
  const exposureType = columnId.slice(0, -EXPOSURE_PRIMARY_SUFFIX.length);
  return exposureType || null;
}

export function isExposurePrimaryColumn(columnId: string): boolean {
  return parseExposurePrimaryColumnId(columnId) !== null;
}

/** Extract snake_case exposure types from selected column ids. */
export function extractExposureTypesFromColumnIds(
  columnIds: string[]
): string[] {
  const types: string[] = [];
  const seen = new Set<string>();
  for (const columnId of columnIds) {
    const exposureType = parseExposurePrimaryColumnId(columnId);
    if (exposureType && !seen.has(exposureType)) {
      seen.add(exposureType);
      types.push(exposureType);
    }
  }
  return types;
}

export function buildExposurePrimaryColumnDefs(
  exposures: ExposureType[]
): ContributionColumnDef[] {
  return exposures.map((exposure) => ({
    id: exposureTypeToColumnId(exposure.value),
    name: exposure.label,
    group: "Exposures",
    sortKey: exposureTypeToColumnId(exposure.value),
    isExposurePrimary: true,
    sortable: true,
  }));
}

export function mergeContributionColumnCatalog(
  staticColumns: ContributionColumnDef[],
  exposureColumns: ContributionColumnDef[]
): ContributionColumnDef[] {
  if (exposureColumns.length === 0) return staticColumns;
  return [...staticColumns, ...exposureColumns];
}

export function filterValidContributionColumnIds(
  columnIds: string[],
  availableColumns: Array<Pick<SelectableItem, "id">>
): string[] {
  const available = new Set(availableColumns.map((col) => col.id));
  return columnIds.filter((id) => available.has(id));
}
